import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Comment, CreateCommentResponse, UpdateComment } from '@mmf/contracts';
import { createComment, deleteComment, updateComment } from '../api/client';
import { AttachmentList } from './AttachmentList';
import { AttachmentUpload } from './AttachmentUpload';
import { moderationBadgeText } from './moderationBadge';
import { PostFeedback } from './PostFeedback';
import { ReportForm } from './ReportForm';
import { VoteButtons } from './VoteButtons';

interface CommentThreadProps {
  topicId: string;
  comments: Comment[];
  isLoading: boolean;
  error: boolean;
  /** The signed-in viewer's own id, for Edit/Delete visibility. Undefined while `useMe()` is still loading. */
  principalId: string | undefined;
}

/**
 * Renders the already path-ordered flat list from the API with indentation
 * from `depth` — see prisma/schema.prisma's Comment model for why the API
 * does this with a materialised path rather than the client walking a tree.
 */
export function CommentThread({
  topicId,
  comments,
  isLoading,
  error,
  principalId,
}: CommentThreadProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['comments', topicId] });
    // commentCount lives on the topic row.
    await queryClient.invalidateQueries({ queryKey: ['topic', topicId] });
  };

  return (
    <section aria-labelledby="comments-heading">
      <h3 id="comments-heading">Comments</h3>

      <CommentComposer topicId={topicId} parentCommentId={null} onPosted={invalidate} />

      {isLoading && <p role="status">Loading comments…</p>}

      {error && (
        <p role="alert" data-testid="comments-error">
          Could not load comments.
        </p>
      )}

      {!isLoading && !error && comments.length === 0 && (
        <p className="hint">No comments yet — be the first to reply.</p>
      )}

      <ul className="comment-list">
        {comments.map((comment) => (
          <CommentRow
            key={comment.id}
            topicId={topicId}
            comment={comment}
            isOwnComment={principalId === comment.authorId}
            principalId={principalId}
            replyingTo={replyingTo}
            setReplyingTo={setReplyingTo}
            invalidate={invalidate}
          />
        ))}
      </ul>
    </section>
  );
}

function CommentRow({
  topicId,
  comment,
  isOwnComment,
  principalId,
  replyingTo,
  setReplyingTo,
  invalidate,
}: {
  topicId: string;
  comment: Comment;
  isOwnComment: boolean;
  principalId: string | undefined;
  replyingTo: string | null;
  setReplyingTo: (id: string | null) => void;
  invalidate: () => Promise<void>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [body, setBody] = useState(comment.body);

  const update = useMutation({
    mutationFn: (input: UpdateComment) => updateComment(comment.id, input),
    onSuccess: async () => {
      setIsEditing(false);
      await invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteComment(comment.id),
    onSuccess: invalidate,
  });

  return (
    <li
      className="comment-row"
      style={{ '--depth': comment.depth } as React.CSSProperties}
      data-testid={`comment-${comment.id}`}
    >
      <p className="hint">
        {comment.authorDisplayName}
        {moderationBadgeText(comment.moderationState) && (
          <span className="badge" data-testid={`held-badge-${comment.id}`}>
            {' '}
            · {moderationBadgeText(comment.moderationState)}
          </span>
        )}
        {comment.updatedAt.getTime() !== comment.createdAt.getTime() && (
          <span data-testid={`edited-marker-${comment.id}`}> · (edited)</span>
        )}
      </p>

      {isEditing ? (
        <form
          className="comment-composer"
          onSubmit={(event) => {
            event.preventDefault();
            update.mutate({ body });
          }}
        >
          <label htmlFor={`edit-comment-body-${comment.id}`}>Edit your comment</label>
          <textarea
            id={`edit-comment-body-${comment.id}`}
            value={body}
            rows={3}
            required
            onChange={(event) => setBody(event.target.value)}
          />

          {update.error && (
            <p role="alert" className="notice warning">
              Could not save your changes. Try again.
            </p>
          )}

          <div className="row">
            <button
              type="submit"
              className="button small"
              disabled={update.isPending || body.trim() === ''}
              data-testid={`save-comment-edit-${comment.id}`}
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="button secondary small"
              onClick={() => {
                setBody(comment.body);
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="comment-body">{comment.body}</div>
      )}

      <AttachmentList kind="comment" id={comment.id} principalId={principalId} />

      <div className="comment-actions">
        <VoteButtons
          kind="comment"
          id={comment.id}
          score={comment.voteScore}
          myVote={comment.myVote}
          invalidateKeys={[['comments', topicId]]}
        />
        <button
          type="button"
          className="button secondary small"
          onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
          data-testid={`reply-${comment.id}`}
        >
          Reply
        </button>

        {isOwnComment && !isEditing && (
          <button
            type="button"
            className="button secondary small"
            onClick={() => setIsEditing(true)}
            data-testid={`edit-comment-${comment.id}`}
          >
            Edit
          </button>
        )}

        {isOwnComment && !isConfirmingDelete && (
          <button
            type="button"
            className="button secondary small"
            onClick={() => setIsConfirmingDelete(true)}
            data-testid={`delete-comment-${comment.id}`}
          >
            Delete
          </button>
        )}

        <ReportForm kind="comment" id={comment.id} />
      </div>

      {isConfirmingDelete && (
        <p className="notice warning">
          Delete this comment? This can't be undone.{' '}
          <button
            type="button"
            className="button small"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            data-testid={`confirm-delete-comment-${comment.id}`}
          >
            {remove.isPending ? 'Deleting…' : 'Confirm delete'}
          </button>{' '}
          <button type="button" className="button secondary small" onClick={() => setIsConfirmingDelete(false)}>
            Cancel
          </button>
        </p>
      )}

      {remove.error && (
        <p role="alert" className="notice warning">
          Could not delete this comment. Try again.
        </p>
      )}

      {isOwnComment && (
        <AttachmentUpload
          kind="comment"
          id={comment.id}
          onUploaded={() => queryClient.invalidateQueries({ queryKey: ['attachments', 'comment', comment.id] })}
        />
      )}

      {replyingTo === comment.id && (
        <CommentComposer
          topicId={topicId}
          parentCommentId={comment.id}
          onPosted={async () => {
            setReplyingTo(null);
            await invalidate();
          }}
        />
      )}
    </li>
  );
}

interface CommentComposerProps {
  topicId: string;
  parentCommentId: string | null;
  onPosted: () => void | Promise<void>;
}

function CommentComposer({ topicId, parentCommentId, onPosted }: CommentComposerProps): React.JSX.Element {
  const [body, setBody] = useState('');
  const [postFeedback, setPostFeedback] = useState<CreateCommentResponse | null>(null);

  const mutation = useMutation({
    mutationFn: () => createComment(topicId, { body, parentCommentId: parentCommentId ?? undefined }),
    onSuccess: async (result) => {
      setBody('');
      setPostFeedback(result);
      await onPosted();
    },
  });

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  const fieldId = parentCommentId ? `reply-body-${parentCommentId}` : 'comment-body';
  const submitTestId = parentCommentId ? `submit-reply-${parentCommentId}` : 'submit-comment';

  return (
    <form onSubmit={handleSubmit} className="comment-composer">
      <label htmlFor={fieldId}>{parentCommentId ? 'Write a reply' : 'Add a comment'}</label>
      <textarea
        id={fieldId}
        value={body}
        rows={3}
        required
        onChange={(event) => setBody(event.target.value)}
      />

      {mutation.error && (
        <p role="alert" className="notice warning">
          Could not post that. Try again.
        </p>
      )}

      {postFeedback && (
        <PostFeedback
          supportNotice={postFeedback.supportNotice}
          isHeld={postFeedback.comment.moderationState !== 'Published'}
        />
      )}

      <button
        type="submit"
        className="button"
        disabled={mutation.isPending || body.trim() === ''}
        data-testid={submitTestId}
      >
        {mutation.isPending ? 'Posting…' : 'Post'}
      </button>
    </form>
  );
}
